import { useCallback } from 'react';
import { loadRazorpayCheckout } from '../../utils/razorpay';

/**
 * Shared Razorpay checkout-opening primitive — extracted 2026-11-01
 * (solution-architect review Rank 3) from THREE independent copies
 * (UpgradeModal, TopupModal, FixPaymentDialog), each of which constructed its
 * own `new window.Razorpay({...})` and wired the same three callbacks by
 * hand. Those three copies had already started to drift (FixPaymentDialog
 * hand-duplicated a THIRD poll-delay array instead of importing
 * activation.ts's one) — this is the one place that behavior now lives.
 *
 * Deliberately NOT a generic "do a checkout" abstraction: each caller still
 * owns its own API call to fetch `checkout_payload`, its own
 * billing_profile_required handling, and its own post-payment poll — those
 * differ enough per flow (renewal vs. upgrade vs. topup vs. fix-payment) that
 * folding them in here would just recreate the three-way branch this hook
 * exists to avoid. This hook's job starts where all three already agreed:
 * given a payload and three callbacks, open Razorpay the same way every
 * time.
 */
export interface RazorpayCheckoutHandlers {
  /** Razorpay's own client-side success callback. The authoritative state
   *  change still arrives via webhook -> engine -> push, so this must never
   *  grant anything itself — callers use it to start their own poll, or (for
   *  a deferred first charge) to move to a "scheduled" stage instead. */
  onPaid: () => void;
  /** The lawyer closed the checkout overlay themselves. This is NOT proof
   *  that no payment happened: a UPI app-switch can complete the payment
   *  without `onPaid` ever firing. Callers must not treat this as a failure. */
  onDismiss: () => void;
  /** Razorpay's own client-side decline signal (a card was rejected, etc.) —
   *  distinct from `onDismiss` (the lawyer closing the window themselves). */
  onFailed: () => void;
}

export function useRazorpayCheckout() {
  const openCheckout = useCallback(
    async (payload: Record<string, unknown>, handlers: RazorpayCheckoutHandlers): Promise<void> => {
      await loadRazorpayCheckout();
      if (!window.Razorpay) {
        throw new Error('Razorpay Checkout failed to load');
      }
      const razorpay = new window.Razorpay({
        ...payload,
        handler: handlers.onPaid,
        modal: { ondismiss: handlers.onDismiss },
      });
      razorpay.on('payment.failed', handlers.onFailed);
      razorpay.open();
    },
    [],
  );

  return { openCheckout };
}
